import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from './lib/session';
import type { StaffRole } from './lib/session';
import { Loading } from './components/ui';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { Devices } from './pages/Devices';
import { DeviceDetail } from './pages/DeviceDetail';
import { Alerts } from './pages/Alerts';
import { HrQueue } from './pages/HrQueue';
import { Commands } from './pages/Commands';
import { Policies } from './pages/Policies';
import { Enrolment } from './pages/Enrolment';
import { AuditTrail } from './pages/AuditTrail';
import { Retention } from './pages/Retention';
import { Offboarding } from './pages/Offboarding';

interface NavEntry {
  to: string;
  label: string;
  group: string;
  roles: StaffRole[];
}

const NAVIGATION: NavEntry[] = [
  { to: '/', label: 'Dashboard', group: 'Overview', roles: ['TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'] },
  { to: '/devices', label: 'Devices', group: 'Fleet', roles: ['TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'] },
  { to: '/enrolment', label: 'Enrolment', group: 'Fleet', roles: ['TECH_ADMIN', 'AUDITOR'] },
  { to: '/policies', label: 'Policy', group: 'Fleet', roles: ['TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'] },
  { to: '/alerts', label: 'Alerts', group: 'Security', roles: ['TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'] },
  { to: '/commands', label: 'Commands', group: 'Security', roles: ['TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'] },
  { to: '/hr-queue', label: 'HR queue', group: 'People', roles: ['HR_REVIEWER', 'AUDITOR'] },
  { to: '/offboarding', label: 'Offboarding', group: 'People', roles: ['TECH_ADMIN', 'HR_REVIEWER', 'AUDITOR'] },
  { to: '/audit', label: 'Audit trail', group: 'Governance', roles: ['TECH_ADMIN', 'AUDITOR'] },
  { to: '/retention', label: 'Retention', group: 'Governance', roles: ['TECH_ADMIN', 'AUDITOR'] },
];

export function App() {
  const { user, loading, signOut, can } = useSession();

  if (loading) return <div className="loading" style={{ padding: 40 }}>Restoring your session&hellip;</div>;
  if (!user) return <SignIn />;

  const visible = NAVIGATION.filter((entry) => can(...entry.roles));
  const groups = [...new Set(visible.map((entry) => entry.group))];

  // An HR_REVIEWER has no dashboard, so send them to the one page they can actually use.
  const landing = can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR') ? '/' : '/hr-queue';

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <strong>Rosabon Sentinel</strong>
          <span>Endpoint Management</span>
        </div>

        <nav className="nav">
          {groups.map((group) => (
            <div key={group}>
              <div className="group">{group}</div>
              <div className="group-links">
                {visible
                  .filter((entry) => entry.group === group)
                  .map((entry) => (
                    <NavLink key={entry.to} to={entry.to} end={entry.to === '/'}>
                      {entry.label}
                    </NavLink>
                  ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="whoami">
          <div className="name">{user.fullName}</div>
          <div className="roles">{user.roles.map((role) => role.replace('_', ' ').toLowerCase()).join(' · ')}</div>
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      </aside>

      <main className="content">
        {user.mustChangePassword && (
          <div className="notice warn">
            You are still using a temporary password. Change it before doing anything else &mdash; this
            console can lock and erase company devices.
          </div>
        )}

        <Routes>
          <Route path="/" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <Dashboard />, landing)} />
          <Route path="/devices" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <Devices />, landing)} />
          <Route path="/devices/:id" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <DeviceDetail />, landing)} />
          <Route path="/enrolment" element={guard(can('TECH_ADMIN', 'AUDITOR'), <Enrolment />, landing)} />
          <Route path="/policies" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <Policies />, landing)} />
          <Route path="/alerts" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <Alerts />, landing)} />
          <Route path="/commands" element={guard(can('TECH_ADMIN', 'SEC_ANALYST', 'AUDITOR'), <Commands />, landing)} />
          <Route path="/hr-queue" element={guard(can('HR_REVIEWER', 'AUDITOR'), <HrQueue />, landing)} />
          <Route path="/offboarding" element={guard(can('TECH_ADMIN', 'HR_REVIEWER', 'AUDITOR'), <Offboarding />, landing)} />
          <Route path="/audit" element={guard(can('TECH_ADMIN', 'AUDITOR'), <AuditTrail />, landing)} />
          <Route path="/retention" element={guard(can('TECH_ADMIN', 'AUDITOR'), <Retention />, landing)} />
          <Route path="*" element={<Navigate to={landing} replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Route-level role gating.
 *
 * <p>This is a usability measure, not the security boundary: the API enforces every role check
 * independently, so a user who reaches a route they should not see gets a 403 from the server
 * rather than data.
 */
function guard(allowed: boolean, element: JSX.Element, fallback: string): JSX.Element {
  return allowed ? element : <Navigate to={fallback} replace />;
}

export { Loading };
